import { Spinner } from '@app/components/icons/spinner';
import type { TranscriptionStatus as TranscriptionStatusType } from '@app/routes/content/domain';
import { useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { VariableSizeList } from 'react-window';

import { TranscriptSegment } from '../../../domain';
import {
  selectFilteredTranscriptSegments,
  selectRecordingTranscript,
  selectRecordingTranscriptionStatus,
  selectTranscriptSearchQuery,
} from '../../../selector';
import { useVideoPlayer } from '../../video-player-context';
import { getSegmentGroupSize, SegmentGroup } from './segment-group';
import { reduceSegmentToSegmentGroups } from './utils';

export const SegmentList = () => {
  const { status: fetchTranscriptStatus } = useSelector(
    selectRecordingTranscript,
  );
  const transcriptionStatus = useAppSelector(
    selectRecordingTranscriptionStatus,
  );
  const filteredSegments = useAppSelector(
    selectFilteredTranscriptSegments,
  ) as TranscriptSegment[];
  const searchQuery = useAppSelector(selectTranscriptSearchQuery);
  const { playbackState } = useVideoPlayer();
  const listRef = useRef<VariableSizeList>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevGroupIndexRef = useRef<number>(-1);
  const isAutoScrolling = useRef<boolean>(false);
  const autoScrollEnabled = useRef<boolean>(true);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const segmentGroups = useMemo(() => {
    return reduceSegmentToSegmentGroups(filteredSegments);
  }, [filteredSegments]);

  const activeSegmentInfo = useMemo(() => {
    const currentTime = playbackState.currentTime;
    if (!segmentGroups.length || currentTime === 0) return null;

    for (let groupIndex = 0; groupIndex < segmentGroups.length; groupIndex++) {
      const group = segmentGroups[groupIndex];
      const segmentIndex = group.segments.findIndex(
        segment => currentTime >= segment.start && currentTime <= segment.end,
      );
      if (segmentIndex >= 0) {
        return {
          groupIndex,
          segmentIndex,
          segment: group.segments[segmentIndex],
        };
      }
    }
    return null;
  }, [segmentGroups, playbackState.currentTime]);

  const isTranscriptionNotReady = transcriptionStatus !== 'ready';
  const isTranscriptLoading = fetchTranscriptStatus === 'processing';
  const hasSegmentGroups = Boolean(segmentGroups?.length);

  const GroupRenderer = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const group = segmentGroups[index];
      if (!group) return null;

      return (
        <div style={{ ...style }}>
          <div className="mx-4">
            <SegmentGroup
              group={group}
              index={index}
              activeSegmentIndex={
                activeSegmentInfo?.groupIndex === index
                  ? activeSegmentInfo.segmentIndex
                  : -1
              }
              searchQuery={searchQuery}
            />
          </div>
        </div>
      );
    },
    [segmentGroups, activeSegmentInfo, searchQuery],
  );

  const getGroupSize = useCallback(
    (index: number) => {
      return getSegmentGroupSize(
        segmentGroups[index],
        containerRef.current?.clientWidth || 372,
      );
    },
    [segmentGroups],
  );

  const handleScroll = useCallback(() => {
    if (isAutoScrolling.current) return;
    autoScrollEnabled.current = false;
  }, []);

  useEffect(() => {
    autoScrollEnabled.current = true;
  }, [playbackState.isPlaying]);

  useEffect(() => {
    if (activeSegmentInfo && listRef.current && autoScrollEnabled.current) {
      const currentGroupIndex = activeSegmentInfo.groupIndex;
      const shouldScrollGroup = currentGroupIndex !== prevGroupIndexRef.current;

      // Scroll group into view using virtual list (only when group changes)
      if (shouldScrollGroup) {
        listRef.current.scrollToItem(currentGroupIndex, 'start');
        prevGroupIndexRef.current = currentGroupIndex;
      }

      // Scroll specific segment into view using DOM (always)
      const delay = shouldScrollGroup ? 150 : 50; // Less delay if no group scroll needed
      const timeoutId = setTimeout(() => {
        const segmentElement = document.querySelector(
          `[data-group-index="${activeSegmentInfo.groupIndex}"] [data-segment-index="${activeSegmentInfo.segmentIndex}"]`,
        );
        segmentElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }, delay);

      isAutoScrolling.current = true;
      setTimeout(() => {
        isAutoScrolling.current = false;
      }, 200);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [activeSegmentInfo]);

  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let rafId: number | null = null;
    let lastHeight: number | null = null;

    const setupObserver = () => {
      const container = containerRef.current;
      if (!container) return;

      const initialHeight = container.clientHeight;
      if (initialHeight > 0) {
        setContainerHeight(initialHeight);
        lastHeight = initialHeight;
      }

      resizeObserver = new ResizeObserver(entries => {
        // Cancel any pending RAF to debounce updates
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }

        rafId = requestAnimationFrame(() => {
          const containerEntry = entries.find(
            entry => entry.target === container,
          );
          if (containerEntry) {
            const { height } = containerEntry.contentRect;
            // Only update if height actually changed to prevent loops
            if (lastHeight === null || Math.abs(height - lastHeight) > 0.5) {
              lastHeight = height;
              setContainerHeight(height);
            }
          }
          rafId = null;
        });
      });

      resizeObserver.observe(container);
    };

    requestAnimationFrame(setupObserver);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (listRef.current && containerHeight !== null) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [containerHeight, segmentGroups]);

  return (
    <div
      id="segment-list"
      ref={containerRef}
      className="flex-1 w-full h-full pb-4"
    >
      {isTranscriptionNotReady ? (
        <TranscriptionStatus status={transcriptionStatus} />
      ) : isTranscriptLoading ? (
        <TranscriptLoading />
      ) : !hasSegmentGroups ? (
        <NoTranscriptFound searchQuery={searchQuery} />
      ) : (
        <VariableSizeList
          ref={listRef}
          height={containerHeight}
          width="100%"
          itemCount={segmentGroups.length}
          itemSize={getGroupSize}
          className="px-4 [scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]"
          onScroll={handleScroll}
        >
          {GroupRenderer}
        </VariableSizeList>
      )}
    </div>
  );
};

const TranscriptionStatus = ({
  status,
}: {
  status: Omit<TranscriptionStatusType, 'ready'> | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col items-center justify-center py-12 text-center"
      style={{ height: '400px' }}
    >
      <h3 className="text-sm font-medium text-white mb-1 leading-5">
        {t(
          `content-recording.content-menu.transcription-status.${
            status || 'unknown'
          }`,
        )}
      </h3>
    </div>
  );
};

const TranscriptLoading = () => {
  return (
    <div
      className="flex flex-col items-center justify-center py-12 text-center"
      style={{ height: '300px' }}
    >
      <Spinner size="medium" showLabel={true} />
    </div>
  );
};

const NoTranscriptFound = ({ searchQuery = '' }: { searchQuery?: string }) => {
  const { t } = useTranslation();
  const translationKey = searchQuery
    ? 'content-recording.content-menu.no-results-found'
    : 'content-recording.content-menu.no-transcript-found';

  return (
    <div
      className="flex flex-col items-center justify-center py-12 text-center"
      style={{ height: '400px' }}
    >
      <h3 className="text-sm font-medium text-white mb-1 leading-5">
        {t(translationKey)}
      </h3>
    </div>
  );
};
